import React from 'react';
import { useTranslation } from 'react-i18next';
import { BarChart, Bar, XAxis, YAxis, CartesianGrid, Tooltip, Legend, ResponsiveContainer } from 'recharts';

const MONTH_LABELS = ['Jan', 'Feb', 'Mar', 'Apr', 'May', 'Jun', 'Jul', 'Aug', 'Sep', 'Oct', 'Nov', 'Dec'];

function QuarterlyBreakdown({ data, currency, exchangeRate, stockPrice }) {
  const { t } = useTranslation();

  const quarters = Array.isArray(data?.quarters) ? data.quarters : (data?.quarters?.$values || []);
  if (quarters.length === 0) return null;

  const convert = (usd) => {
    if (usd == null) return 0;
    return currency === 'NOK' && exchangeRate ? usd * exchangeRate : usd;
  };

  // Collect all grant years from vesting data
  const allGrantYears = new Set();
  quarters.forEach(q => {
    const vesting = Array.isArray(q.stockVesting) ? q.stockVesting : (q.stockVesting?.$values || []);
    vesting.forEach(v => allGrantYears.add(v.compYear));
  });
  const grantYears = [...allGrantYears].sort();

  // Build monthly chart data from quarterly data
  // Base salary is spread evenly across months
  // Cash bonus lands in September (index 8)
  // Stock vesting lands in the last month of each quarter (Feb=Q1, May=Q2, Aug=Q3, Nov=Q4)
  // ESPP is withheld from salary every month and buys shares at the end of the quarter, so
  // base is shown net of the contribution and the full quarter's contribution reappears as
  // the ESPP bar in the purchase month. Stacking gross base on top of the contribution would
  // count the same kroner twice.
  const vestMonths = [1, 4, 7, 10]; // Feb, May, Aug, Nov (0-indexed)
  const bonusMonth = 8; // September

  // The contribution was withheld in NOK, so prefer the rate stored on the purchase.
  const esppNOK = (quarter, field) => {
    const stored = quarter?.[`espp${field}NOK`];
    if (stored != null) return stored;
    const usd = quarter?.[`espp${field}USD`] || 0;
    return exchangeRate ? usd * exchangeRate : 0;
  };

  const toDisplay = (nok) => {
    if (currency === 'NOK') return nok;
    return exchangeRate ? nok / exchangeRate : 0;
  };

  const chartData = MONTH_LABELS.map((label, monthIdx) => {
    const quarterIdx = Math.floor(monthIdx / 3); // 0-3
    const quarter = quarters[quarterIdx];
    if (!quarter) return { name: label, base: 0 };

    const quarterEsppNOK = esppNOK(quarter, 'Contribution');
    const monthlyBaseNOK = (quarter.baseSalaryNOK || 0) / 3 - quarterEsppNOK / 3;
    const monthlyBase = toDisplay(Math.max(monthlyBaseNOK, 0));

    const entry = { name: label, base: monthlyBase };

    // Cash bonus in September only (already in NOK)
    entry.bonus = monthIdx === bonusMonth
      ? (currency === 'NOK' ? quarter.cashBonusNOK : (exchangeRate ? quarter.cashBonusNOK / exchangeRate : 0))
      : 0;

    // ESPP and vesting in vest months (last month of quarter)
    if (vestMonths.includes(monthIdx)) {
      entry.espp = toDisplay(quarterEsppNOK);
      entry.esppDiscount = toDisplay(esppNOK(quarter, 'DiscountGain'));
      const vesting = Array.isArray(quarter.stockVesting) ? quarter.stockVesting : (quarter.stockVesting?.$values || []);
      grantYears.forEach(year => {
        const vest = vesting.find(v => v.compYear === year);
        const shares = vest?.totalShares || 0;
        entry[`vest_${year}`] = convert(shares * (stockPrice || 0));
      });
    } else {
      entry.espp = 0;
      entry.esppDiscount = 0;
      grantYears.forEach(year => { entry[`vest_${year}`] = 0; });
    }

    return entry;
  });

  const COLORS = ['#3b82f6', '#8b5cf6', '#f59e0b', '#ef4444', '#10b981', '#ec4899', '#06b6d4'];
  const currLabel = currency === 'NOK' ? 'kr' : '$';

  const formatValue = (value) => {
    if (value >= 1000) return `${(value / 1000).toFixed(0)}k`;
    return value.toFixed(0);
  };

  return (
    <div className="quarterly-breakdown">
      <h3>{t('compensation.monthlyBreakdown', t('compensation.quarterlyBreakdown'))}</h3>
      <ResponsiveContainer width="100%" height={400}>
        <BarChart data={chartData} margin={{ top: 20, right: 30, left: 20, bottom: 5 }}>
          <CartesianGrid strokeDasharray="3 3" stroke="var(--surface-border)" />
          <XAxis dataKey="name" stroke="var(--text-muted)" />
          <YAxis stroke="var(--text-muted)" tickFormatter={v => `${currLabel}${formatValue(v)}`} />
          <Tooltip
            formatter={(value, name) => [`${currLabel}${Math.round(value).toLocaleString()}`, name]}
            contentStyle={{ backgroundColor: 'var(--surface-card)', border: '1px solid var(--surface-border)', borderRadius: '8px' }}
            labelStyle={{ color: 'var(--text-primary)' }}
            itemStyle={{ color: 'var(--text-secondary)' }}
          />
          <Legend />
          <Bar dataKey="base" name={t('compensation.baseSalary')} stackId="comp" fill="#3b82f6" />
          <Bar dataKey="bonus" name={t('compensation.cashBonus')} stackId="comp" fill="#10b981" />
          {grantYears.map((year, i) => (
            <Bar
              key={year}
              dataKey={`vest_${year}`}
              name={`${t('compensation.stockGrant')} ${year}`}
              stackId="comp"
              fill={COLORS[(i + 2) % COLORS.length]}
            />
          ))}
          <Bar dataKey="espp" name={t('compensation.esppFromSalary')} stackId="comp" fill="#8b5cf6" />
          <Bar dataKey="esppDiscount" name={t('compensation.esppDiscountGain')} stackId="comp" fill="#a78bfa" />
        </BarChart>
      </ResponsiveContainer>
      <p className="chart-note">{t('compensation.esppChartNote')}</p>
    </div>
  );
}

export default QuarterlyBreakdown;
